'use client';

import * as React from 'react';

import type { CityCode } from '@loci/city-editions';
import { useDiscoverFacetsQuery } from '@loci/api';
import type { DiscoverFacetCounts } from '@loci/types';

import {
  DiscoverFacetChecklistView,
  FACET_CLEAR_ALL,
  type DiscoverFacetChecklistItem,
  type DiscoverFacetCountLabelFormatter,
} from '@/components/molecules/DiscoverFacetChecklist';
import { CATEGORY_LABELS, CATEGORY_ORDER } from '@/config/categories';
import { categoryDisplayLabel } from '@/lib/city-home/categoryLabelCase';
import { useDiscoverParams } from '@/hooks/useDiscoverParams';
import {
  formatDiscoverCategoryHeadScope,
  resolveDiscoverScopeKey,
} from '@/lib/discover/resultsCountCopy';
import { useDiscoverEventArgs } from '@/lib/discover/useDiscoverEventArgs';
import { formatEventCount } from '@/lib/format/eventCount';

const TITLE = 'Category';

/**
 * Zero-count caption text (WEB-659 / DPL-09). `"NONE TONIGHT"` reads
 * naturally only for the `tonight` date preset; every other date scope
 * (tomorrow, weekend, next7/30, a custom range, or no date filter at all)
 * uses the generic `"NO ACTIVE EVENTS"` so the caption never lies about the
 * word "tonight" when the user isn't actually looking at tonight.
 */
function zeroCountCaptionForDate(date: string | undefined): string {
  return date === 'tonight' ? 'None tonight' : 'No active events';
}

/**
 * Trailing caption for one Category row (WEB-828).
 *
 * A bare number is ambiguous the moment this facet is filtering. Category
 * counts are computed "against neighbors" — the active `?category=` is
 * excluded from its own facet pass — so a row's count means "events in this
 * category matching every OTHER active filter". Which side of the filter those
 * events are currently on depends entirely on whether the row is selected:
 *
 * - selected → they are in the list right now → `SHOWING 34 EVENTS`
 * - unselected, while some other category is selected → they are exactly what
 *   the filter is holding back → `12 HIDDEN EVENTS`
 * - nothing selected → no category is filtered in or out, so neither verb is
 *   true. Fall through (`undefined`) to the plain numeric count.
 * - a genuine zero → fall through to the `NONE TONIGHT` / `NO ACTIVE EVENTS`
 *   zero-state caption. Post-WEB-828 that caption is accurate: the row really
 *   has no events under the other active filters. The rows the ticket called
 *   out as misleading were the ones the facet bug had falsely zeroed, and they
 *   now render a real `## HIDDEN EVENTS`.
 */
const categoryCountCaption: DiscoverFacetCountLabelFormatter = (item, state) => {
  if (!state.hasSelection || state.isDisabled || item.count === 0) return undefined;
  // Pluralization + the pinned-locale thousands separator both come from the
  // shared `formatEventCount` (WEB-831). Thousands-separated for the same
  // reason the plain numeric count is, and matching the numeric branch in
  // `DiscoverFacetChecklistView`, which renders in the same rail.
  return state.isChecked
    ? `Showing ${formatEventCount(item.count, { separateThousands: true, casing: 'sentence' })}`
    : formatEventCount(item.count, {
        qualifier: 'hidden',
        separateThousands: true,
        casing: 'sentence',
      });
};

/**
 * Stable per-row checkbox id (`category-music`, `category-sports`, …). Two
 * upstream callers depend on this:
 *
 *  1. React's form-field accessibility heuristic — without an `id` on the
 *     checkbox, the audit at 2026-05-14 emitted "form-field without an id
 *     attribute" warnings for every Category row. Stamping the id quiets the
 *     warning without changing the visual surface (the `aria-label` already
 *     drives screen-reader naming).
 *
 *  2. Future E2E selectors can lock onto `#category-music` instead of relying
 *     on test-ids, which is the audit-preferred convention for primary
 *     navigation surfaces.
 *
 * @see WEB-265
 */
function categoryInputId(item: DiscoverFacetChecklistItem): string {
  return `category-${item.slug}`;
}

/**
 * Build the canonical Charlotte category list with all counts set to zero.
 * Used as the deterministic fallback when the facets API is loading or has
 * errored out — keeps the Category rail useable so users can still filter,
 * even when counts are temporarily unavailable. Tracked in the issue note
 * for WEB-59 (M2.4 implementation note: "fall back to a stub adapter that
 * returns the categories from `apps/web/config/categories.ts` with count 0").
 */
function buildFallbackItems(): DiscoverFacetChecklistItem[] {
  return CATEGORY_ORDER.map((slug) => ({
    slug,
    label: categoryDisplayLabel(CATEGORY_LABELS[slug]),
    count: 0,
  }));
}

/**
 * Coerce the runtime `facets.categories` payload into a stable
 * `Map<slug, count>`. Mirrors the WEB-264 `toFacetRows` pattern in
 * `DiscoverNeighborhoodFilter.tsx:68-95`: the backend wire is
 * `Array<{slug, count}>` (`DiscoverFacetsWire.categories` in
 * `services/backend-api/src/services/discoverFacets.ts`), but the shared
 * `DiscoverFacetCounts` type historically advertised
 * `Partial<Record<EventCategory, number>>`. Indexing the array as a record
 * always fell through to `0`, which is why every category rendered `(0)`
 * pre-WEB-349. We accept either shape so a future regression on either side
 * (or an out-of-date deploy) never reproduces the silent zero. Rows missing a
 * usable slug are dropped.
 */
function toCategoryCountMap(raw: unknown): Map<string, number> {
  const out = new Map<string, number>();
  if (Array.isArray(raw)) {
    for (const entry of raw) {
      if (!entry || typeof entry !== 'object') continue;
      const candidate = entry as { slug?: unknown; count?: unknown };
      const slug = typeof candidate.slug === 'string' ? candidate.slug.trim() : '';
      if (slug.length === 0) continue;
      const count =
        typeof candidate.count === 'number' && Number.isFinite(candidate.count)
          ? candidate.count
          : 0;
      out.set(slug, count);
    }
    return out;
  }
  if (raw && typeof raw === 'object') {
    for (const [slug, count] of Object.entries(raw as Record<string, unknown>)) {
      const trimmedSlug = slug.trim();
      if (trimmedSlug.length === 0) continue;
      const safeCount = typeof count === 'number' && Number.isFinite(count) ? count : 0;
      out.set(trimmedSlug, safeCount);
    }
  }
  return out;
}

/**
 * True when the payload marks the CATEGORY facet as degraded (API-1679).
 *
 * The backend computes category counts "against neighbors" from a second,
 * category-unfiltered spatial scan. That scan degrades rather than failing the
 * request: on a transient DB error it falls back to the category-scoped
 * candidate set, so every unselected category tallies `0` — which is
 * indistinguishable here from a real "no events in this category". Disabling
 * those rows would turn an unreliable reading into a definite availability
 * claim (`NO ACTIVE EVENTS` on a category that has events), and because the
 * anonymous SSR payload is cached, one blip would repeat that claim to every
 * visitor of the URL.
 *
 * Read defensively for the same reason `toCategoryCountMap` is (WEB-349): the
 * marker is optional and additive, so it is absent from every pre-API-1679
 * producer and from any deploy skew in either direction. Anything that is not
 * an array carrying the literal `'category'` is treated as "not degraded" —
 * the healthy default, never a fabricated degradation.
 */
function isCategoryFacetDegraded(facets: DiscoverFacetCounts | undefined): boolean {
  const degraded: unknown = facets?.degraded;
  return Array.isArray(degraded) && degraded.includes('category');
}

/**
 * Merge facet counts onto the canonical category order. Categories without a
 * count are emitted with `count = 0` so toggling never disappears a row even
 * when the backend omits zero buckets. Accepts either the live wire shape
 * (`Array<{slug, count}>`) or the legacy `Record<slug, number>` shape via
 * {@link toCategoryCountMap} — see the WEB-349 comment there.
 */
function buildItemsFromFacets(raw: unknown): DiscoverFacetChecklistItem[] {
  const counts = toCategoryCountMap(raw);
  return CATEGORY_ORDER.map((slug) => ({
    slug,
    label: categoryDisplayLabel(CATEGORY_LABELS[slug]),
    count: counts.get(slug) ?? 0,
  }));
}

export interface DiscoverCategoryFilterProps {
  /**
   * Active city — used to scope the facets query to the city geo anchor
   * (WEB-263). Without this, category counts are computed globally and
   * include cross-country events.
   */
  readonly cityCode: CityCode;
  /** Wrapper class names forwarded to the underlying view. */
  readonly className?: string;
}

/**
 * Connected wrapper binding `DiscoverFacetChecklistView` to the Category
 * facet. Reads `facets.categories` from `useDiscoverFacetsQuery` (M7.3) and
 * `params.category` from `useDiscoverParams` (M1.1). Toggling a row writes
 * the slug onto the comma-joined `?category=` URL param; the header "Clear"
 * link wipes the param entirely.
 *
 * **Count source contract (WEB-265):** Each row's count comes from the
 * facets payload, NOT from filtering the locally-rendered event array. The
 * audit at 2026-05-14 showed every category reading `(0)` because the prior
 * implementation derived counts from the small, pre-filter event slice that
 * the page held in state — the facets API was already returning
 * Music=560 / Sports=179 / Art=50 / Tech=1 for Charlotte and was being
 * ignored. Producers may omit zero buckets, so `buildItemsFromFacets`
 * substitutes `0` for any missing slot. The canonical ten (`CATEGORY_ORDER`,
 * WEB-606) always render in fixed slot order regardless of which slugs the API
 * actually returned, so a category with no ingested events yet stays visible
 * as `(0)` rather than disappearing from the rail.
 *
 * **Wire-shape note (WEB-349):** The backend emits `Array<{slug, count}>`
 * for `facets.categories`. The shared `DiscoverFacetCounts` type previously
 * advertised `Partial<Record<EventCategory, number>>`, which made
 * record-style indexing fall through to `0` on every row. `toCategoryCountMap`
 * accepts EITHER shape defensively so the rail keeps live counts regardless
 * of which side of the contract drifts first — same pattern as
 * `toFacetRows` in `DiscoverNeighborhoodFilter.tsx` (WEB-264). The shared
 * type has since been aligned to the wire; the coercer remains as the
 * regression guard.
 *
 * **City scoping (WEB-263):** Counts are scoped to the active city via the
 * geo anchor that `buildDiscoverQueryArgs` injects. Without this, counts are
 * computed globally and include cross-country events.
 *
 * **Request coalescing (API-1428):** The chip counts come from the SAME
 * `useDiscoverFacetsQuery` cache entry the list view subscribes to. Both
 * derive their args from the shared {@link buildDiscoverQueryArgs} helper, so
 * RTK Query dedupes the facet-rail subscription onto the list view's in-flight
 * `/events/discover` request instead of firing a second one. The previous
 * implementation passed a city-anchor-only args object (no `view`/`sort`/
 * `date`/…), which produced a different cache key and burned an extra
 * anon-IP request on every cold load (API-1416 audit follow-up).
 *
 * **Multi-select (WEB-828):** this is a checkbox list and it behaves like one
 * — toggling a row unions/removes its slug on the comma-joined `?category=`
 * param, and the list blends every chosen category. It previously *read* as
 * single-select for a reason that lived entirely in the backend: the spatial
 * pre-filter pushes `category` into the candidate SQL (API-1440), and that
 * category-scoped candidate set was also bounding the `exclude: 'category'`
 * facet pass. Every unselected category therefore reported `0`, and the
 * `disableZeroCount` rule below faithfully disabled all of them. The fix
 * (`categoryFacetCandidateIds` in `services/backend-api/src/services/
 * canonicalEventCount.ts`) restores honest neighbor counts, so a zero here now
 * means a real dead end rather than an artifact of the current selection.
 *
 * **Count captions (WEB-828):** because counts are computed against
 * neighbors, a row's number means "events in this category matching every
 * OTHER active filter" — which is `SHOWING n` for a selected row and `n
 * HIDDEN` for an unselected one. See {@link categoryCountCaption}.
 *
 * Resilience contract:
 * - Healthy facets API → render the canonical ten categories with live counts.
 *   A row whose live count is `0` is DISABLED (WEB-659 / DPL-09) — non-clickable,
 *   captioned `NONE TONIGHT` / `NO ACTIVE EVENTS` instead of `(0)`, since
 *   toggling it can only ever return an empty result. The disabled state is
 *   also visually distinct (WEB-828) — previously the row looked and hovered
 *   exactly like an actionable one, so the only way to discover it was dead
 *   was to click it.
 * - Loading → the same ten categories stay visible and interactive, but
 *   counts skeleton (WEB-786). A placeholder `0` would later become a live
 *   count and violate the honest-count contract.
 * - API error → the same ten categories with all counts at `0` (sourced from
 *   `apps/web/config/categories.ts`), every row FULLY INTERACTIVE. That
 *   terminal zero will not flip to a live count unless the query recovers.
 *   Empty-state is intentionally avoided for Category because the surface is
 *   a primary navigation affordance — see WEB-59 implementation note.
 * - Degraded facets (API-1679) → the payload arrived and the counts render,
 *   but `facets.degraded` names `category`, so the backend's second,
 *   category-unfiltered spatial scan failed and every UNSELECTED category was
 *   tallied against the category-scoped candidate set (i.e. `0`). Rows stay
 *   FULLY INTERACTIVE and lose the `NO ACTIVE EVENTS` zero-state caption: a
 *   `0` here is "we could not measure this", not "there is nothing here", and
 *   the rail must not disable a category the user can still usefully click.
 *
 * **Zero-count disabled state (WEB-659 / DPL-09):** a category already
 * selected in the URL whose count drops to zero (e.g. after a date-preset
 * change) stays selected — the URL param is never auto-cleared — but its row
 * renders `checked` + `disabled` and can no longer be toggled from within
 * this checklist. Removal happens via the separate `DiscoverActiveFilters`
 * chip strip, which reads/writes URL state directly and is unaffected by the
 * row-level disabled treatment.
 */
export function DiscoverCategoryFilter({
  cityCode,
  className,
}: DiscoverCategoryFilterProps): React.ReactElement {
  const { params, setParams } = useDiscoverParams();
  // Build the args identically to the list view (`DiscoverListView` /
  // `DiscoverResultsBarConnected`) so RTK Query collapses this facet-rail
  // subscription onto the list view's single `/events/discover` request
  // instead of issuing a parallel one (API-1428).
  const queryArgs = useDiscoverEventArgs(params, cityCode);
  const { currentData, isError } = useDiscoverFacetsQuery(queryArgs);
  // WEB-659 / DPL-09: only disable zero-count rows once we have a CONFIRMED
  // live reading. Loading skeletons the counts (WEB-786) and keeps every row
  // interactive. On API error, `items` falls back to the all-zero canonical
  // list without disabling — a terminal placeholder, not a confirmed "this
  // filter returns nothing" reading.
  const liveCategories = !isError ? currentData?.facets.categories : undefined;
  const hasLiveFacets = liveCategories !== undefined;
  const countsPending = !isError && !hasLiveFacets;
  // API-1679: a served-but-degraded reading is NOT a confirmed one. The counts
  // still render (they are the honest category-scoped tally for the selected
  // rows), but no row may be disabled or captioned "no events" off them.
  const countsDegraded = hasLiveFacets && isCategoryFacetDegraded(currentData?.facets);

  const items = React.useMemo<DiscoverFacetChecklistItem[]>(() => {
    if (liveCategories !== undefined) {
      return buildItemsFromFacets(liveCategories as unknown);
    }
    return buildFallbackItems();
  }, [liveCategories]);

  const handleToggle = React.useCallback(
    (slug: string) => {
      if (slug === FACET_CLEAR_ALL) {
        setParams({ category: [] });
        return;
      }
      const current = params.category;
      const next = current.includes(slug)
        ? current.filter((value) => value !== slug)
        : [...current, slug];
      setParams({ category: next });
    },
    [params.category, setParams],
  );

  const handleClear = React.useCallback(() => {
    setParams({ category: [] });
  }, [setParams]);

  const scopeKey = resolveDiscoverScopeKey(currentData?.facets?.scopeKey, params.date);
  const headScope = formatDiscoverCategoryHeadScope(scopeKey, cityCode);

  return (
    <DiscoverFacetChecklistView
      title={TITLE}
      id="discover-category-filter"
      items={items}
      selected={params.category}
      onToggle={handleToggle}
      onClear={handleClear}
      inputIdForItem={categoryInputId}
      disableZeroCount={hasLiveFacets && !countsDegraded}
      zeroCountLabel={zeroCountCaptionForDate(scopeKey)}
      formatCountLabel={categoryCountCaption}
      countsPending={countsPending}
      headScope={headScope}
      className={className}
    />
  );
}
